import { Dialog } from "@dash/dialog";
import { Button } from "@dash/form";
import { Spinner } from "@dash/spinner";
import { Theme, useTheme } from "@emotion/react";
import React from "react";
import { MdCheckCircle, MdErrorOutline, MdOpenInNew, MdOutlineWarningAmber } from "react-icons/md";

import { DiffSyncDependentChannelRefreshRow } from "./types";

export const DependentRefreshDialog = (props: {
  isRefreshing: boolean;
  rows: DiffSyncDependentChannelRefreshRow[];
  onClose: () => void;
}) => (
  <Dialog
    header={props.isRefreshing ? "Odświeżanie powiązanych kanałów" : "Powiązane zmiany"}
    placement="center"
    onClose={props.isRefreshing ? undefined : props.onClose}
    contentCss={{ width: "min(100%, 34rem)" }}
    footer={
      props.isRefreshing ? undefined : (
        <div css={{ display: "flex", gap: "0.5rem", marginLeft: "auto" }}>
          <Button onClick={props.onClose}>OK</Button>
        </div>
      )
    }
  >
    <div css={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
      {props.rows.map((row) => (
        <DependentRefreshResultRow key={row.channel} row={row} />
      ))}
    </div>
  </Dialog>
);

const DependentRefreshResultRow = (props: { row: DiffSyncDependentChannelRefreshRow }) => {
  const theme = useTheme();
  const status = getDependentRefreshStatus(props.row);
  const statusStyle = getDependentRefreshStatusStyle(theme, status);

  return (
    <div
      css={{
        display: "flex",
        alignItems: "center",
        gap: "0.75rem",
        padding: "0.75rem",
        border: `1px solid ${statusStyle.border}`,
        borderRadius: theme.smallBorderRadius,
        background: statusStyle.background,
      }}
    >
      {statusStyle.icon}
      <div css={{ minWidth: 0, flex: 1 }}>
        <div css={{ fontWeight: 700, color: statusStyle.color }}>{props.row.name ?? props.row.channel}</div>
        <div css={{ fontSize: "0.875rem", color: statusStyle.color }}>{formatDependentRefreshSummary(props.row)}</div>
      </div>
      {props.row.href && (
        <a href={props.row.href} target="_blank" rel="noreferrer" css={{ textDecoration: "none" }}>
          <Button as="div" variant="outline" icon={<MdOpenInNew />}>
            Otwórz
          </Button>
        </a>
      )}
    </div>
  );
};

type DependentRefreshStatus = "loading" | "clean" | "warning" | "danger";

const getDependentRefreshStatus = (row: DiffSyncDependentChannelRefreshRow): DependentRefreshStatus => {
  if (row.isLoading) return "loading";
  if (row.error || (row.errorCount ?? 0) > 0) return "danger";
  if ((row.changeCount ?? 0) > 0) return "warning";

  return "clean";
};

const getDependentRefreshStatusStyle = (
  theme: Theme,
  status: DependentRefreshStatus,
): {
  background: string;
  border: string;
  color: string;
  icon: React.ReactNode;
} => {
  if (status === "loading") {
    return {
      background: "white",
      border: theme.colors.primaryLight,
      color: theme.colors.primary,
      icon: <Spinner color={theme.colors.primary} />,
    };
  }

  if (status === "danger") {
    return {
      background: theme.colors.dangerBackground,
      border: theme.colors.danger,
      color: theme.colors.danger,
      icon: <MdErrorOutline size={20} color={theme.colors.danger} />,
    };
  }

  if (status === "warning") {
    return {
      background: theme.colors.warningBackground,
      border: theme.colors.warning,
      color: theme.colors.warning,
      icon: <MdOutlineWarningAmber size={20} color={theme.colors.warning} />,
    };
  }

  return {
    background: "white",
    border: theme.colors.success,
    color: theme.colors.success,
    icon: <MdCheckCircle size={20} color={theme.colors.success} />,
  };
};

const formatDependentRefreshSummary = (row: DiffSyncDependentChannelRefreshRow) => {
  if (row.isLoading) return "Odświeżanie";
  if (row.error) return row.error;

  const changeCount = row.changeCount ?? 0;
  const errorCount = row.errorCount ?? 0;

  if (errorCount > 0) return `Zmiany: ${changeCount}, błędy: ${errorCount}`;
  if (changeCount > 0) return `Zmiany: ${changeCount}`;

  return "Brak zmian";
};
